import CalcInput from "@/components/calculators/CalcInput"
import ResultCard from "@/components/calculators/ResultCard"
import PageHero from "@/components/PageHero"
import SEOHead from "@/components/SEOHead"
import { formatCurrencyRounded, formatRatio, safeDivide, toNumber } from "@/lib/format"
import { useState } from "react"

function GoogleAdsRoas() {
  const [spend, setSpend] = useState("1000")
  const [cpc, setCpc] = useState("1.50")
  const [convRate, setConvRate] = useState("3")
  const [aov, setAov] = useState("80")

  const spendNum = toNumber(spend)
  const cpcNum = toNumber(cpc)
  const convRateNum = toNumber(convRate)
  const aovNum = toNumber(aov)

  const predictedClicks = safeDivide(spendNum, cpcNum)
  const predictedConversions = predictedClicks * (convRateNum / 100)
  const projectedCost = spendNum
  const projectedRevenue = predictedConversions * aovNum
  const roas = safeDivide(projectedRevenue, projectedCost)
  const roi = safeDivide(projectedRevenue - projectedCost, projectedCost) * 100

  return (
    <div>
      <SEOHead
        title="Google Ads ROAS Calculator"
        description="Project clicks, conversions, revenue, and ROAS for your Google Ads search campaigns using CPC, conversion rate, and average order value."
      />

      <PageHero
        eyebrow="Search network"
        title="Google Ads ROAS Calculator"
        description="Project clicks, conversions, revenue, and ROAS for your Google Search campaigns."
      />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 -mt-6 sm:-mt-8 pb-12">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 sm:p-7">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-5">Campaign inputs</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <CalcInput
                  label="Ad Spend"
                  prefix="$"
                  value={spend}
                  onChange={setSpend}
                  placeholder="1000"
                  tooltip="Total Google Ads budget for the campaign or period being modeled."
                />
                <CalcInput
                  label="Avg. CPC"
                  prefix="$"
                  value={cpc}
                  onChange={setCpc}
                  placeholder="1.50"
                  tooltip="Average Cost Per Click. Find this in your Google Ads campaign reporting."
                />
                <CalcInput
                  label="Conversion Rate"
                  suffix="%"
                  value={convRate}
                  onChange={setConvRate}
                  placeholder="3"
                  tooltip="Percentage of clicks that result in a completed sale or conversion."
                />
                <CalcInput
                  label="Avg. Order Value"
                  prefix="$"
                  value={aov}
                  onChange={setAov}
                  placeholder="80"
                  tooltip="Average revenue generated per completed order or conversion."
                />
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 mb-5">Projected results</p>
              <div className="grid grid-cols-2 gap-3">
                <ResultCard label="Predicted Clicks" value={predictedClicks.toFixed(0)} formula="Ad Spend / Avg. CPC" />
                <ResultCard label="Predicted Conversions" value={predictedConversions.toFixed(1)} formula="Clicks x Conversion Rate" />
                <ResultCard label="Projected Cost" value={formatCurrencyRounded(projectedCost)} formula="Equal to Ad Spend" />
                <ResultCard label="Projected Revenue" value={formatCurrencyRounded(projectedRevenue)} formula="Conversions x Avg. Order Value" />
                <ResultCard label="ROAS" value={formatRatio(roas)} emphasis formula="Projected Revenue / Projected Cost" />
                <ResultCard label="ROI" value={`${roi.toFixed(2)}%`} formula="((Revenue - Cost) / Cost) x 100" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-16">
        <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-4">Target ROAS & Automated Bidding</h2>
        <p className="text-[15px] text-slate-600 leading-relaxed mb-4">
          Google Ads' Target ROAS (tROAS) bidding strategy uses machine learning to automatically set bids that
          maximize conversion value while hitting your specified return target. The algorithm needs sufficient
          conversion volume and accurate conversion value tracking to perform well — typically 30+ conversions
          in the past 30 days.
        </p>
        <p className="text-[15px] text-slate-600 leading-relaxed">
          Search network benchmarks vary widely by industry, but a ROAS between 2:1 and 4:1 is common for e-commerce
          search campaigns, with high-intent branded terms often exceeding 8:1. Use this calculator to model
          different CPC and conversion rate scenarios before adjusting live bids or budgets.
        </p>
      </section>
    </div>
  )
}

export default GoogleAdsRoas
